import React, { useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import {
  Container, Row, Col, Button, Card, Alert, Badge, Breadcrumb,
  Form, InputGroup, ListGroup, Modal, Navbar, Nav, Offcanvas,
  Pagination, ProgressBar, Spinner, Table, Tabs, Tab, Toast,
  ToastContainer, Accordion, Dropdown, ButtonGroup
} from 'react-bootstrap';

const Lesson20_Bootstrap = () => {
  const [showModal, setShowModal] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [progress, setProgress] = useState(60);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  useEffect(() => {
    console.log('📖 Lesson 20: Bootstrap in React - Loaded');
    console.log('🎨 React Bootstrap provides Bootstrap components as React components');
  }, []);

  const handleModalShow = () => {
    console.log('🎨 Bootstrap Modal: Opening');
    setShowModal(true);
  };

  const handleModalClose = () => {
    console.log('🎨 Bootstrap Modal: Closing');
    setShowModal(false);
  };

  const handleOffcanvasShow = () => {
    console.log('🎨 Bootstrap Offcanvas: Opening');
    setShowOffcanvas(true);
  };

  const handleOffcanvasClose = () => {
    console.log('🎨 Bootstrap Offcanvas: Closing');
    setShowOffcanvas(false);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    console.log('📝 Bootstrap Form Submitted:', formData);
    setShowToast(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <LessonLayout 
      lessonNumber={20}
      title="Bootstrap in React" 
      description="Comprehensive guide to using React Bootstrap components"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>React Bootstrap component library</li>
          <li>Grid system (Container, Row, Col)</li>
          <li>Forms, Buttons, Cards, Modals</li>
          <li>Navigation components</li>
          <li>Alerts, Badges, Tables</li>
          <li>And many more Bootstrap components!</li>
        </ul>
      </Alert>

      {/* Section 1: Grid System */}
      <h4 className="mt-4">1. Grid System (Container, Row, Col)</h4>
      <Card className="mb-3">
        <Card.Body>
          <Container>
            <Row className="mb-2">
              <Col className="bg-primary text-white p-3 border">Col 1</Col>
              <Col className="bg-success text-white p-3 border">Col 2</Col>
              <Col className="bg-danger text-white p-3 border">Col 3</Col>
            </Row>
            <Row>
              <Col md={4} className="bg-warning p-3 border">md=4</Col>
              <Col md={8} className="bg-info text-white p-3 border">md=8</Col>
            </Row>
          </Container>
        </Card.Body>
      </Card>

      {/* Section 2: Buttons */}
      <h4 className="mt-4">2. Buttons</h4>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="success">Success</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="warning">Warning</Button>
            <Button variant="info">Info</Button>
            <Button variant="light">Light</Button>
            <Button variant="dark">Dark</Button>
            <Button variant="link">Link</Button>
          </div>
          <div className="d-flex gap-2 flex-wrap mt-3">
            <Button variant="outline-primary">Outline Primary</Button>
            <Button variant="outline-success">Outline Success</Button>
            <Button variant="outline-danger">Outline Danger</Button>
          </div>
          <div className="d-flex gap-2 flex-wrap mt-3">
            <Button size="lg">Large Button</Button>
            <Button>Default Button</Button>
            <Button size="sm">Small Button</Button>
          </div>
        </Card.Body>
      </Card>

      {/* Section 3: Cards */}
      <h4 className="mt-4">3. Cards</h4>
      <Row>
        <Col md={4}>
          <Card className="mb-3">
            <Card.Img variant="top" src="https://placehold.co/300x150" />
            <Card.Body>
              <Card.Title>Card with Image</Card.Title>
              <Card.Text>Cards are flexible content containers with various options.</Card.Text>
              <Button variant="primary">Learn More</Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="mb-3" border="primary">
            <Card.Header className="bg-primary text-white">Featured</Card.Header>
            <Card.Body>
              <Card.Title>Card with Header</Card.Title>
              <Card.Text>Some quick example text.</Card.Text>
            </Card.Body>
            <Card.Footer className="text-muted">2 days ago</Card.Footer>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="mb-3" bg="dark" text="white">
            <Card.Body>
              <Card.Title>Dark Card</Card.Title>
              <Card.Text>Card with dark background and white text.</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Section 4: Alerts */}
      <h4 className="mt-4">4. Alerts</h4>
      <Alert variant="primary">This is a primary alert—check it out!</Alert>
      <Alert variant="success">
        <Alert.Heading>Success!</Alert.Heading>
        <p>Your changes have been saved successfully.</p>
        <hr />
        <p className="mb-0">You can now continue with your work.</p>
      </Alert>
      <Alert variant="danger" dismissible>
        <strong>Error!</strong> Something went wrong. Please try again.
      </Alert>

      {/* Section 5: Badges */}
      <h4 className="mt-4">5. Badges</h4>
      <Card className="mb-3">
        <Card.Body>
          <h5>Heading <Badge bg="primary">New</Badge></h5>
          <div className="d-flex gap-2 flex-wrap">
            <Badge bg="primary">Primary</Badge>
            <Badge bg="secondary">Secondary</Badge>
            <Badge bg="success">Success</Badge>
            <Badge bg="danger">Danger</Badge>
            <Badge bg="warning" text="dark">Warning</Badge>
            <Badge bg="info">Info</Badge>
            <Badge bg="light" text="dark">Light</Badge>
            <Badge bg="dark">Dark</Badge>
          </div>
          <div className="d-flex gap-2 flex-wrap mt-3">
            <Badge pill bg="primary">Pill Badge</Badge>
            <Badge pill bg="success">99+</Badge>
            <Badge pill bg="danger">New</Badge>
          </div>
        </Card.Body>
      </Card>

      {/* Section 6: Forms */}
      <h4 className="mt-4">6. Forms</h4>
      <Card className="mb-3">
        <Card.Header>Contact Form</Card.Header>
        <Card.Body>
          <Form onSubmit={handleFormSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">We'll never share your information.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <InputGroup>
                <InputGroup.Text>@</InputGroup.Text>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                name="message"
                rows={3}
                value={formData.message}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check type="checkbox" label="Subscribe to newsletter" />
            </Form.Group>

            <Button type="submit" variant="primary">Submit Form</Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Section 7: Modal */}
      <h4 className="mt-4">7. Modal</h4>
      <Card className="mb-3">
        <Card.Body>
          <Button variant="primary" onClick={handleModalShow}>
            Open Modal
          </Button>

          <Modal show={showModal} onHide={handleModalClose}>
            <Modal.Header closeButton>
              <Modal.Title>Modal Title</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>This is a Bootstrap Modal in React!</p>
              <p>Modals are great for dialogs, confirmations, and focused forms.</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleModalClose}>
                Close
              </Button>
              <Button variant="primary" onClick={handleModalClose}>
                Save Changes
              </Button>
            </Modal.Footer>
          </Modal>
        </Card.Body>
      </Card>

      {/* Section 8: Offcanvas */}
      <h4 className="mt-4">8. Offcanvas</h4>
      <Card className="mb-3">
        <Card.Body>
          <Button variant="primary" onClick={handleOffcanvasShow}>
            Open Offcanvas
          </Button>

          <Offcanvas show={showOffcanvas} onHide={handleOffcanvasClose} placement="end">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>Offcanvas</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <p>This is an offcanvas sidebar!</p>
              <ListGroup>
                <ListGroup.Item>Item 1</ListGroup.Item>
                <ListGroup.Item>Item 2</ListGroup.Item>
                <ListGroup.Item>Item 3</ListGroup.Item>
              </ListGroup>
            </Offcanvas.Body>
          </Offcanvas>
        </Card.Body>
      </Card>

      {/* Section 9: Tables */}
      <h4 className="mt-4">9. Tables</h4>
      <Card className="mb-3">
        <Card.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>John Doe</td>
                <td><Badge bg="success">Active</Badge></td>
                <td><Button size="sm" variant="outline-primary">Edit</Button></td>
              </tr>
              <tr>
                <td>2</td>
                <td>Jane Smith</td>
                <td><Badge bg="warning">Pending</Badge></td>
                <td><Button size="sm" variant="outline-primary">Edit</Button></td>
              </tr>
              <tr>
                <td>3</td>
                <td>Bob Johnson</td>
                <td><Badge bg="danger">Inactive</Badge></td>
                <td><Button size="sm" variant="outline-primary">Edit</Button></td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Section 10: Progress Bars */}
      <h4 className="mt-4">10. Progress Bars</h4>
      <Card className="mb-3">
        <Card.Body>
          <ProgressBar now={progress} label={`${progress}%`} className="mb-3" />
          <ProgressBar striped variant="success" now={40} className="mb-3" />
          <ProgressBar animated variant="warning" now={75} className="mb-3" />
          <div className="d-flex gap-2">
            <Button size="sm" onClick={() => setProgress(Math.max(0, progress - 10))}>Decrease</Button>
            <Button size="sm" onClick={() => setProgress(Math.min(100, progress + 10))}>Increase</Button>
          </div>
        </Card.Body>
      </Card>

      {/* Section 11: Spinners */}
      <h4 className="mt-4">11. Spinners & Loading States</h4>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex gap-3 flex-wrap align-items-center">
            <Spinner animation="border" variant="primary" />
            <Spinner animation="border" variant="success" />
            <Spinner animation="grow" variant="danger" />
            <Spinner animation="grow" variant="warning" />
            <Button variant="primary" disabled>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Loading...
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Section 12: Accordion */}
      <h4 className="mt-4">12. Accordion</h4>
      <Accordion defaultActiveKey="0" className="mb-3">
        <Accordion.Item eventKey="0">
          <Accordion.Header>What is React Bootstrap?</Accordion.Header>
          <Accordion.Body>
            React Bootstrap replaces Bootstrap's JavaScript with React components.
            It provides pure React components with no jQuery dependency.
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="1">
          <Accordion.Header>Why use React Bootstrap?</Accordion.Header>
          <Accordion.Body>
            - No jQuery dependency<br />
            - React components instead of JavaScript<br />
            - Better integration with React apps<br />
            - Customizable and themeable
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="2">
          <Accordion.Header>How to install?</Accordion.Header>
          <Accordion.Body>
            <code>npm install react-bootstrap bootstrap</code>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* Section 13: Pagination */}
      <h4 className="mt-4">13. Pagination</h4>
      <Card className="mb-3">
        <Card.Body>
          <Pagination>
            <Pagination.First />
            <Pagination.Prev />
            <Pagination.Item>{1}</Pagination.Item>
            <Pagination.Item active>{2}</Pagination.Item>
            <Pagination.Item>{3}</Pagination.Item>
            <Pagination.Item>{4}</Pagination.Item>
            <Pagination.Item>{5}</Pagination.Item>
            <Pagination.Next />
            <Pagination.Last />
          </Pagination>
        </Card.Body>
      </Card>

      {/* Section 14: Dropdown */}
      <h4 className="mt-4">14. Dropdowns</h4>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex gap-2">
            <Dropdown>
              <Dropdown.Toggle variant="primary">
                Dropdown Button
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item href="#/action-1">Action</Dropdown.Item>
                <Dropdown.Item href="#/action-2">Another action</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item href="#/action-3">Something else</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <ButtonGroup>
              <Button variant="success">Split Button</Button>
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle split variant="success" />
                <Dropdown.Menu>
                  <Dropdown.Item>Option 1</Dropdown.Item>
                  <Dropdown.Item>Option 2</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </ButtonGroup>
          </div>
        </Card.Body>
      </Card>

      {/* Toast Container */}
      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
          <Toast.Header>
            <strong className="me-auto">Form Submitted</strong>
            <small>just now</small>
          </Toast.Header>
          <Toast.Body>Your form has been submitted successfully!</Toast.Body>
        </Toast>
      </ToastContainer>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Installation:</strong>
        <pre className="mb-2 mt-2 bg-dark text-white p-2 rounded">
          npm install react-bootstrap bootstrap
        </pre>
        <strong>Import in your app:</strong>
        <pre className="mb-0 mt-2 bg-dark text-white p-2 rounded">
          import 'bootstrap/dist/css/bootstrap.min.css';
        </pre>
      </Alert>

      <Alert variant="info" className="mt-3">
        <strong>📚 Resources:</strong>
        <ul className="mb-0">
          <li>Official Docs: <a href="https://react-bootstrap.github.io/" target="_blank" rel="noopener noreferrer">react-bootstrap.github.io</a></li>
          <li>Bootstrap Docs: <a href="https://getbootstrap.com/" target="_blank" rel="noopener noreferrer">getbootstrap.com</a></li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson20_Bootstrap;
